import type { ComponentType } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { cn } from "@/shared/lib/utils";

interface NavItemProps {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  collapsed: boolean;
  onNavigate?: () => void;
}

/**
 * Item individual del sidebar.
 *
 * Visual:
 *   - Idle: muted-foreground + icono delgado.
 *   - Hover: fondo accent muy sutil + text foreground.
 *   - Active: barrita primary a la izquierda + text foreground + icono
 *     primary. Fondo neutro (nada de bg-primary agresivo).
 *   - Collapsed: solo icono, Tooltip al hover para accesibilidad.
 */
export function NavItem({ to, label, icon: Icon, collapsed, onNavigate }: NavItemProps) {
  const location = useLocation();
  const isExactHome = to === "/";
  const isActive = isExactHome ? location.pathname === "/" : location.pathname.startsWith(to);

  const link = (
    <NavLink
      to={to}
      onClick={onNavigate}
      end={isExactHome}
      className={({ isActive: navActive }) => {
        const active = navActive || isActive;
        return cn(
          "group relative flex h-10 items-center gap-3 rounded-lg px-3 text-[13.5px] transition-colors duration-150",
          "text-muted-foreground hover:bg-accent/40 hover:text-foreground",
          active && "bg-primary/5 text-foreground hover:bg-primary/5",
          collapsed && "justify-center px-0",
        );
      }}
    >
      {({ isActive: navActive }) => {
        const active = navActive || isActive;
        return (
          <>
            {/* Indicador lateral activo */}
            <span
              aria-hidden
              className={cn(
                "absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-primary transition-opacity duration-200",
                active ? "opacity-100" : "opacity-0",
              )}
            />
            <Icon
              className={cn(
                "h-[18px] w-[18px] shrink-0 transition-colors duration-200",
                active ? "text-primary" : "text-muted-foreground group-hover:text-foreground",
              )}
            />
            <span
              className={cn(
                "truncate font-medium transition-all duration-200",
                active && "text-foreground",
                collapsed && "pointer-events-none w-0 overflow-hidden opacity-0",
              )}
            >
              {label}
            </span>
          </>
        );
      }}
    </NavLink>
  );

  if (!collapsed) return link;

  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={12}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}
